import { supabase } from "../supabase/supabaseClient";

export interface UserProfile {
  id: string;
  name?: string;
  full_name?: string;
  email?: string;
  phone?: string;
  avatar?: string;
  role: "initial" | "user" | "disapproved" | "admin";
  created_at: string;
  updated_at: string;
}

export interface UserManagementFilters {
  role?: "initial" | "user" | "disapproved" | "admin";
  search?: string;
}

export interface PaginationParams {
  page?: number;
  limit?: number;
}

export interface UserManagementResponse {
  data: UserProfile[];
  count: number;
  error: string | null;
}

export class UserManagementService {
  // Get all users with filtering and pagination (optimized for large datasets)
  static async getUsers(
    filters: UserManagementFilters = {},
    pagination: PaginationParams = {}
  ): Promise<UserManagementResponse> {
    try {
      const { page = 1, limit = 10 } = pagination;
      const from = (page - 1) * limit;
      const to = from + limit - 1;

      // Select only necessary columns for better performance
      let query = supabase
        .from("base_profiles")
        .select(
          "id, name, full_name, email, phone, avatar, role, created_at, updated_at",
          { count: "exact" }
        )
        .order("created_at", { ascending: false });

      // Apply filters first for better performance
      if (filters.role) {
        query = query.eq("role", filters.role);
      }

      if (filters.search) {
        // Use more efficient search with proper indexing
        const searchTerm = filters.search.trim();
        if (searchTerm) {
          query = query.or(
            `name.ilike.%${searchTerm}%,full_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`
          );
        }
      }

      // Apply pagination last
      query = query.range(from, to);

      const { data, error, count } = await query;

      if (error) {
        throw error;
      }

      return {
        data: data || [],
        count: count || 0,
        error: null,
      };
    } catch (error) {
      console.error("Error fetching users:", error);
      return {
        data: [],
        count: 0,
        error: error instanceof Error ? error.message : "Failed to fetch users",
      };
    }
  }

  // Get user statistics (optimized for large datasets using aggregation)
  static async getUserStats(): Promise<{
    data: {
      total: number;
      initial: number;
      approved: number;
      disapproved: number;
      admin: number;
    } | null;
    error: string | null;
  }> {
    try {
      // Use multiple optimized queries for better performance with large datasets
      const [
        totalResult,
        initialResult,
        approvedResult,
        disapprovedResult,
        adminResult,
      ] = await Promise.all([
        supabase.from("base_profiles").select("*", { count: "exact", head: true }),
        supabase
          .from("base_profiles")
          .select("*", { count: "exact", head: true })
          .eq("role", "initial"),
        supabase
          .from("base_profiles")
          .select("*", { count: "exact", head: true })
          .eq("role", "user"),
        supabase
          .from("base_profiles")
          .select("*", { count: "exact", head: true })
          .eq("role", "disapproved"),
        supabase
          .from("base_profiles")
          .select("*", { count: "exact", head: true })
          .eq("role", "admin"),
      ]);

      // Check for any errors
      const errors = [
        totalResult.error,
        initialResult.error,
        approvedResult.error,
        disapprovedResult.error,
        adminResult.error,
      ].filter(Boolean);
      if (errors.length > 0) {
        throw new Error(errors[0]?.message || "Failed to fetch statistics");
      }

      const stats = {
        total: totalResult.count || 0,
        initial: initialResult.count || 0,
        approved: approvedResult.count || 0,
        disapproved: disapprovedResult.count || 0,
        admin: adminResult.count || 0,
      };

      return { data: stats, error: null };
    } catch (error) {
      console.error("Error fetching user stats:", error);
      return {
        data: null,
        error:
          error instanceof Error
            ? error.message
            : "Failed to fetch user statistics",
      };
    }
  }

  // Approve a user (change role from initial/disapproved to user)
  static async approveUser(
    userId: string
  ): Promise<{ success: boolean; error: string | null }> {
    try {
      const { error } = await supabase
        .from("base_profiles")
        .update({
          role: "user",
          updated_at: new Date().toISOString(),
        })
        .eq("id", userId);

      if (error) {
        throw error;
      }

      return { success: true, error: null };
    } catch (error) {
      console.error("Error approving user:", error);
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Failed to approve user",
      };
    }
  }

  // Disapprove a user (change role to disapproved)
  static async disapproveUser(
    userId: string
  ): Promise<{ success: boolean; error: string | null }> {
    try {
      const { error } = await supabase
        .from("base_profiles")
        .update({
          role: "disapproved",
          updated_at: new Date().toISOString(),
        })
        .eq("id", userId);

      if (error) {
        throw error;
      }

      return { success: true, error: null };
    } catch (error) {
      console.error("Error disapproving user:", error);
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Failed to disapprove user",
      };
    }
  }

  // Bulk approve multiple users
  static async bulkApproveUsers(
    userIds: string[]
  ): Promise<{ success: boolean; error: string | null }> {
    try {
      const { error } = await supabase
        .from("base_profiles")
        .update({
          role: "user",
          updated_at: new Date().toISOString(),
        })
        .in("id", userIds);

      if (error) {
        throw error;
      }

      return { success: true, error: null };
    } catch (error) {
      console.error("Error bulk approving users:", error);
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Failed to approve users",
      };
    }
  }

  // Get pending users count (for notifications)
  static async getPendingUsersCount(): Promise<{
    count: number;
    error: string | null;
  }> {
    try {
      const { count, error } = await supabase
        .from("base_profiles")
        .select("*", { count: "exact", head: true })
        .eq("role", "initial");

      if (error) {
        throw error;
      }

      return { count: count || 0, error: null };
    } catch (error) {
      console.error("Error fetching pending users count:", error);
      return {
        count: 0,
        error:
          error instanceof Error
            ? error.message
            : "Failed to fetch pending users count",
      };
    }
  }

  // Check if user signup blocking is bypassed
  static isSignupBlockBypassed(): boolean {
    return import.meta.env.VITE_BYPASS_SIGNUP_BLOCK === "true";
  }
}
